import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useModalState from '@/hooks/pure/useModalState';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import { IMPORT_STATE, type KustomerInfo } from '../types';
import KustomerConnectModal from './KustomerConnectModal';
import { useKustomer } from './useKustomer';

export const KustomerItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const {
    kustomerWorkspace,
    disconnectKustomerMutation,
    requestImportMutation,
    setKustomerWorkspace,
  } = useKustomer(!isConnector);

  const connectModalState = useModalState();
  const importModalState = useModalState<{ isNew: boolean }>();

  const handleConnectComplete = (workspace: KustomerInfo) => {
    if (!currWorkspace) return;

    setKustomerWorkspace(workspace);
    connectModalState.close();
  };

  const handleDisconnect = () => {
    disconnectKustomerMutation();
  };

  const handleImport = (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!kustomerWorkspace}
        source={SOURCE_TYPE.KUSTOMER}
        description="Import userchats as feedback into Syncly."
        connectedDisplay={kustomerWorkspace?.domain || 'Kustomer'}
        info={kustomerWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(workspace) =>
          workspace.import_state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <Button
              variant="outlined"
              onClick={() => importModalState.open({ modalState: { isNew: false } })}
            >
              One-time import
            </Button>
          )
        }
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import Conversations'}
          subTitle={'Kustomer'}
          subTitleList={kustomerWorkspace?.domain}
          historyList={kustomerWorkspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImport({ start_date, end_date })
          }
        />
      </Modal>
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
        useBackgroundClose
      >
        <KustomerConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
    </>
  );
};
